import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { HealthInformationCopilot } from "@/modules/chats/services/ai/chatHealthInformationCopilotQuery"

export type SuggestedCondition = HealthInformationCopilot["conditions"][number]
export type SuggestedAllergy = HealthInformationCopilot["allergies"][number]
export type SuggestedMedication = HealthInformationCopilot["medications"][number]

interface CopilotSuggestionBaseState<TItem> {
  data: TItem[]
  selected: TItem | null
  hide: (item: TItem) => void
  setSelected: (item: TItem | null) => void
  resetSelected: () => void
}

interface HealthInformationCopilotState {
  conditions: CopilotSuggestionBaseState<SuggestedCondition>
  allergies: CopilotSuggestionBaseState<SuggestedAllergy>
  medications: CopilotSuggestionBaseState<SuggestedMedication>
  setHealthInformationCopilot: (healthInformation: {
    conditions: SuggestedCondition[]
    allergies: SuggestedAllergy[]
    medications: SuggestedMedication[]
  }) => void
}

type HealthInformationCopilotStore = ReturnType<typeof createHealthInformationCopilotStore>

const createHealthInformationCopilotStore = () => {
  return create<HealthInformationCopilotState>((set) => ({
    conditions: {
      data: [],
      selected: null,
      hide: (conditionToHide: SuggestedCondition) =>
        set((state) => ({
          conditions: {
            ...state.conditions,
            data: state.conditions.data.filter((condition) => condition !== conditionToHide),
          },
        })),
      setSelected: (selectedCondition) =>
        set((state) => ({
          conditions: { ...state.conditions, selected: selectedCondition },
        })),
      resetSelected: () =>
        set((state) => ({
          conditions: { ...state.conditions, selected: null },
        })),
    },

    allergies: {
      data: [],
      selected: null,
      hide: (allergyToHide: SuggestedAllergy) =>
        set((state) => ({
          allergies: {
            ...state.allergies,
            data: state.allergies.data.filter((allergy) => allergy !== allergyToHide),
          },
        })),
      setSelected: (selectedAllergy) =>
        set((state) => ({
          allergies: { ...state.allergies, selected: selectedAllergy },
        })),
      resetSelected: () =>
        set((state) => ({
          allergies: { ...state.allergies, selected: null },
        })),
    },

    medications: {
      data: [],
      selected: null,
      hide: (medicationToHide: SuggestedMedication) =>
        set((state) => ({
          medications: {
            ...state.medications,
            data: state.medications.data.filter((medication) => medication !== medicationToHide),
          },
        })),
      setSelected: (selectedMedication) =>
        set((state) => ({
          medications: { ...state.medications, selected: selectedMedication },
        })),
      resetSelected: () =>
        set((state) => ({
          medications: { ...state.medications, selected: null },
        })),
    },

    setHealthInformationCopilot: (healthInformation) =>
      set((state) => ({
        conditions: { ...state.conditions, data: [...healthInformation.conditions] },
        allergies: { ...state.allergies, data: [...healthInformation.allergies] },
        medications: { ...state.medications, data: [...healthInformation.medications] },
      })),
  }))
}

export const HealthInformationCopilotContext = createContext<HealthInformationCopilotStore | null>(null)

export const HealthInformationCopilotProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<HealthInformationCopilotStore>(undefined)
  if (!storeRef.current) {
    storeRef.current = createHealthInformationCopilotStore()
  }

  return (
    <HealthInformationCopilotContext.Provider value={storeRef.current}>
      {children}
    </HealthInformationCopilotContext.Provider>
  )
}

export function useHealthInformationCopilotContext<T>(selector: (state: HealthInformationCopilotState) => T): T {
  const store = useContext(HealthInformationCopilotContext)
  if (!store) throw new Error("Missing HealthInformationCopilotContext.Provider in the tree")

  return useStore(store, selector)
}
